import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useUploadFile } from '@/hooks/use-upload-file';
import { useVideoFrameExtractor } from '@/hooks/use-video-frame-extractor';
import { cropFrameDataUrl } from '@/lib/cover/cropFrameDataUrl';
import { frameDataUrlToFile } from '@/lib/media/frameCapture';
import { slidesNeedingFrameCapture } from '@/lib/quoteCarousel/frameCaptureTargets';
import { frameExtractorUrl } from '@/lib/quoteCarousel/frameExtractorUrl';
import {
  resolveFrameCapturePlan, type FrameCaptureSources,
} from '@/lib/quoteCarousel/resolveFrameCapturePlan';
import { SLIDE_FORMATS } from '@/lib/quoteCarousel/slideFormats';
import type { QuoteCarousel, QuoteSlide } from '@/lib/quoteCarousel/types';

const CAPTURE_OPTIONS = {
  canvasWidth: 1080,
  canvasHeight: 1920,
  quality: 0.92,
  sampleFps: 30,
  fitToSource: true,
  maxCachedFrames: 4,
};

export interface UseCarouselFrameCaptureInput {
  carousel: QuoteCarousel | null;
  setCarousel: Dispatch<SetStateAction<QuoteCarousel | null>>;
  /** `original` = the project's/library's full source video; `edited` = the
   *  rendered `finished_video_url`. A null source makes every pending frame
   *  targeting it fall back to color. */
  sources: FrameCaptureSources;
  /** Kept intervals (source axis, playback order) — maps onto the export. */
  intervals: Array<{ start: number; end: number }>;
  /** `clip.data.transitions` — overlap transitions shift the export axis. */
  transitions?: unknown;
  /** `clip.data.coverImage` — a cover prepends one frame to the export. */
  coverImage?: unknown;
  /** `clip.data.cropCenterKeyframes` (source axis); frames are cropped
   *  around the reframe center at each quote's time. Absent = centered. */
  cropCenterKeyframes?: unknown;
  /** Color used when a capture fails (the brand background). */
  fallbackColor: string;
  /** False until the carousel is loaded (and while the clip is read-only). */
  enabled: boolean;
}

/** Identity of the capture a slide currently wants: a finished capture is
 *  written back only if the slide still wants exactly this one. */
function captureKey(slide: QuoteSlide): string {
  const bg = slide.background;
  if (bg?.kind !== 'frame') return `${slide.id}:none`;
  return `${slide.id}:${bg.source}:${bg.time ?? 'auto'}:${slide.start}:${slide.end}`;
}

/**
 * Fills every `{ kind: 'frame', url: null }` slide background with a frame
 * grabbed from the slide's chosen source (`resolveFrameCapturePlan`):
 * - original: the full source video at the quote's source time
 *   `min(start + 0.3, end)` (or the picked source time), cropped around the
 *   clip's reframe center at that time;
 * - edited + automatic: the same source time mapped onto the rendered
 *   export's timeline (scene layout + transitions + cover frame), centered;
 * - an edited pick is used as-is on the render.
 *
 * Extractors load `frameExtractorUrl` (CORS cache key), never persisted.
 *
 * Captures run one at a time: extract (1080×1920 box, source aspect) → crop
 * to the slide aspect → upload → store the https URL. A data URL
 * is never persisted and the upload is NOT registered in My Media. A failed
 * capture turns that slide's background into `fallbackColor` and reports it
 * through `failed` so the page can show an inline notice.
 */
export function useCarouselFrameCapture({
  carousel,
  setCarousel,
  sources,
  intervals,
  transitions,
  coverImage,
  cropCenterKeyframes,
  fallbackColor,
  enabled,
}: UseCarouselFrameCaptureInput) {
  const { getUploadUrl } = useUploadFile();
  const { extractFrame: extractOriginal } = useVideoFrameExtractor(frameExtractorUrl(sources.original), CAPTURE_OPTIONS);
  const { extractFrame: extractEdited } = useVideoFrameExtractor(frameExtractorUrl(sources.edited), CAPTURE_OPTIONS);
  const [capturingId, setCapturingId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loopTick, setLoopTick] = useState(0);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const carouselRef = useRef(carousel);
  carouselRef.current = carousel;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const captureOne = useCallback(
    async (slide: QuoteSlide, aspect: number): Promise<string | null> => {
      // No source, or an unknown export axis: fail, never guess a frame.
      const plan = resolveFrameCapturePlan({
        sources, intervals, transitions, coverImage, cropCenterKeyframes, slide,
      });
      if (!plan) return null;
      const bg = slide.background;
      const extractFrame = bg?.kind === 'frame' && bg.source === 'edited' ? extractEdited : extractOriginal;
      try {
        const { time, centerX } = plan;
        const raw = await extractFrame(time);
        if (!raw) return null;
        let framed = raw;
        try {
          framed = await cropFrameDataUrl(raw, { targetAspect: aspect, centerX });
        } catch {
          // Keep the full frame; QuoteSlide cover-fits its background.
        }
        const frame = frameDataUrlToFile(framed, time);
        const file = new File([frame], `quote-frame-${Math.round(time * 1000)}.jpg`, { type: frame.type });
        const url = await getUploadUrl(file);
        return url && url.startsWith('https://') ? url : null;
      } catch {
        return null;
      }
    },
    [coverImage, cropCenterKeyframes, extractEdited, extractOriginal, getUploadUrl, intervals, sources, transitions],
  );

  const pendingKey = slidesNeedingFrameCapture(carousel).map(captureKey).join('|');

  useEffect(() => {
    const current = carouselRef.current;
    if (!enabled || !current || !pendingKey || inFlightRef.current) return;
    const pending = slidesNeedingFrameCapture(current);
    const { width, height } = SLIDE_FORMATS[current.format];
    inFlightRef.current = true;
    // A new pass starts: the previous pass's failure notice is stale.
    setFailed(false);

    void (async () => {
      try {
        for (const slide of pending) {
          if (!mountedRef.current) return;
          setCapturingId(slide.id);
          const url = await captureOne(slide, width / height);
          if (!mountedRef.current) return;
          if (!url) setFailed(true);
          // Only fill a slide that still wants this frame (the user may have
          // switched it to a color or deleted it meanwhile).
          setCarousel((prev) => {
            if (!prev) return prev;
            let changed = false;
            const slides = prev.slides.map((entry) => {
              if (entry.id !== slide.id) return entry;
              const bg = entry.background;
              if (bg?.kind !== 'frame' || bg.url !== null) return entry;
              // The quote moved, or its source/picked time changed while
              // capturing: leave it pending for the next pass.
              if (captureKey(entry) !== captureKey(slide)) return entry;
              changed = true;
              return {
                ...entry,
                background: url
                  ? { ...bg, url }
                  : { kind: 'color' as const, color: fallbackColor },
              };
            });
            return changed ? { ...prev, slides } : prev;
          });
        }
      } finally {
        inFlightRef.current = false;
        if (mountedRef.current) {
          setCapturingId(null);
          // Slides added while this pass ran are picked up by the next one.
          setLoopTick((tick) => tick + 1);
        }
      }
    })();
    // The carousel is read through a ref: `pendingKey` alone decides whether
    // a new pass is needed, so unrelated edits never restart capture.
  }, [enabled, pendingKey, loopTick, captureOne, fallbackColor, setCarousel]);

  return {
    /** Slide currently being captured, if any. */
    capturingId,
    /** Whether some slide is still waiting for a frame that WILL be captured.
     *  Disabled (e.g. read-only clip) never captures, so it never waits: those
     *  slides render with the brand background color. */
    capturing: enabled && pendingKey !== '',
    /** A capture failed and fell back to a color background. */
    failed,
    dismissFailed: useCallback(() => setFailed(false), []),
  };
}
